import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Pencil, Save, X, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, SECTIONS, formatAnswer, SCALE_LABELS } from "../lib/questionnaire";

const QuestionEditor = ({ q, value, allDraft, onChange }) => {
  if (q.type === "scale") {
    const labels = q.labels || SCALE_LABELS;
    return (
      <div>
        <div className="flex justify-between text-xs text-[#A29F9B] mb-2">
          <span>{labels[0]}</span><span>{labels[2]}</span>
        </div>
        <div className="flex gap-2 justify-between" data-testid={`edit-${q.key}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => onChange(q.key, n)}
              className={`flex-1 py-2 rounded-lg text-sm border transition-colors ${
                value === n ? "border-[#C5A880] bg-[#C5A880]/10 text-[#C5A880]" : "border-white/10 text-[#A29F9B] hover:border-white/20"
              }`}>{n}</button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "choice") {
    return (
      <div className="grid sm:grid-cols-2 gap-2" data-testid={`edit-${q.key}`}>
        {q.options.map((o) => (
          <button key={o.v} onClick={() => onChange(q.key, o.v)}
            className={`text-sm py-2 px-3 rounded-lg border transition-colors text-left ${
              value === o.v ? "border-[#C5A880] bg-[#C5A880]/10 text-[#C5A880]" : "border-white/10 text-[#DCD7D2] hover:border-white/20"
            }`}>{o.l}</button>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const sel = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const next = sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v];
      if (q.max && next.length > q.max) return;
      onChange(q.key, next);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={`edit-${q.key}`}>
        {q.options.map((o) => (
          <button key={o} onClick={() => toggle(o)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              sel.includes(o) ? "border-[#C5A880] bg-[#C5A880]/10 text-[#C5A880]" : "border-white/10 text-[#DCD7D2] hover:border-white/20"
            }`}>{o}</button>
        ))}
      </div>
    );
  }
  if (q.type === "keywords") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <input type="text" value={arr.join(", ")}
        onChange={(e) => onChange(q.key, e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
        placeholder="Três palavras separadas por vírgulas"
        className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C5A880]/50 focus:outline-none"
        data-testid={`edit-${q.key}`} />
    );
  }
  if (q.type === "text") {
    return (
      <textarea value={value || ""} onChange={(e) => onChange(q.key, e.target.value)}
        rows={3} maxLength={q.maxLength} placeholder={q.placeholder}
        className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C5A880]/50 focus:outline-none"
        data-testid={`edit-${q.key}`} />
    );
  }
  if (q.type === "number") {
    return (
      <input type="number" min={q.min} max={q.max} value={value ?? ""}
        onChange={(e) => onChange(q.key, parseInt(e.target.value || "0", 10))}
        className="w-32 bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C5A880]/50 focus:outline-none"
        data-testid={`edit-${q.key}`} />
    );
  }
  if (q.type === "range_pair") {
    const lo = allDraft[q.minKey] ?? q.defMin;
    const hi = allDraft[q.maxKey] ?? q.defMax;
    return (
      <div className="flex items-center gap-3" data-testid={`edit-${q.key}`}>
        <input type="number" min={q.min} max={q.max} value={lo}
          onChange={(e) => onChange(q.minKey, parseInt(e.target.value || "0", 10))}
          className="w-24 bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <span className="text-[#A29F9B]">a</span>
        <input type="number" min={q.min} max={q.max} value={hi}
          onChange={(e) => onChange(q.maxKey, parseInt(e.target.value || "0", 10))}
          className="w-24 bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <span className="text-xs text-[#A29F9B]">cm</span>
      </div>
    );
  }
  return <span className="text-[#A29F9B] text-sm">Tipo desconhecido</span>;
};

const SectionRow = ({ q, value, allAnswers }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5 last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-[#F5F2EE] text-sm">{q.title}</p>
      {q.subtitle && <p className="text-xs text-[#A29F9B] mt-0.5">{q.subtitle}</p>}
    </div>
    <span className="text-sm text-[#C5A880] shrink-0 max-w-[55%] text-right">
      {formatAnswer(q, value, allAnswers)}
    </span>
  </div>
);

const AnswersReview = () => {
  const [answers, setAnswers] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await client.get("/questionnaire/answers");
        setAnswers(r.data.answers || {});
      } catch { toast.error("Falha a carregar respostas."); }
    })();
  }, []);

  const startEdit = (sectionName) => {
    setEditingSection(sectionName);
    setDraft({ ...answers });
  };
  const cancelEdit = () => { setEditingSection(null); setDraft({}); };

  const updateField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const saveSection = async () => {
    if (!editingSection) return;
    setSaving(true);
    // Send only the keys for this section (ignore range_pair keys, swap with min/max keys)
    const sectionDef = SECTIONS.find(s => s.name === editingSection);
    const payload = {};
    sectionDef.questions.forEach((q) => {
      if (q.type === "range_pair") {
        payload[q.minKey] = draft[q.minKey];
        payload[q.maxKey] = draft[q.maxKey];
      } else {
        payload[q.key] = draft[q.key];
      }
    });
    try {
      const r = await client.put("/questionnaire/answers", payload);
      setAnswers(r.data.answers);
      toast.success("Respostas atualizadas. Compatibilidade recalculada.");
      setEditingSection(null);
      setDraft({});
    } catch (err) {
      const detail = err?.response?.data?.detail || "Falha ao guardar.";
      toast.error(typeof detail === "string" ? detail : "Respostas inválidas — verifica os campos.");
    } finally { setSaving(false); }
  };

  const completeness = useMemo(() => {
    if (!answers) return 0;
    const total = QUESTIONS.length;
    let filled = 0;
    QUESTIONS.forEach((q) => {
      if (q.type === "range_pair") {
        if (answers[q.minKey] != null && answers[q.maxKey] != null) filled += 1;
      } else {
        const v = answers[q.key];
        if (Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && v !== "")) filled += 1;
      }
    });
    return Math.round((filled / total) * 100);
  }, [answers]);

  if (!answers) return (
    <div className="min-h-screen bg-[#0C0E12]"><Navbar /><div className="p-12 text-[#A29F9B]">A carregar...</div></div>
  );

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="answers-review-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/profile" className="text-sm text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-2 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar ao perfil
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-3.5 h-3.5 text-[#C5A880]" />
          <span className="label-uppercase">Privado · só tu vês</span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl mb-3">As minhas respostas</h1>
        <p className="text-[#A29F9B] mb-2 leading-relaxed">
          Revê o que respondeste e altera o que faça mais sentido. Cada secção pode ser editada
          em separado — a compatibilidade com outros perfis é recalculada automaticamente.
        </p>
        <p className="text-xs text-[#A29F9B] mb-10">
          Inquérito: <span className="text-[#C5A880] font-medium">{completeness}% completo</span>
        </p>

        <div className="space-y-4">
          {SECTIONS.map((s) => {
            const isEditing = editingSection === s.name;
            return (
              <section key={s.name} className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid={`section-${s.name}`}>
                <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                  <div>
                    <span className="label-uppercase">Secção</span>
                    <h2 className="font-display text-xl mt-1">{s.name}</h2>
                  </div>
                  {!isEditing ? (
                    <button onClick={() => startEdit(s.name)} className="btn-secondary text-sm inline-flex items-center gap-1"
                      data-testid={`edit-section-${s.name}`}>
                      <Pencil className="w-4 h-4" /> Editar
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={cancelEdit} className="btn-secondary text-sm inline-flex items-center gap-1"
                        data-testid={`cancel-section-${s.name}`}>
                        <X className="w-4 h-4" /> Cancelar
                      </button>
                      <button onClick={saveSection} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-1"
                        data-testid={`save-section-${s.name}`}>
                        <Save className="w-4 h-4" /> {saving ? "A guardar..." : "Guardar"}
                      </button>
                    </div>
                  )}
                </div>

                {!isEditing ? (
                  <div className="space-y-0">
                    {s.questions.map((q) => (
                      <SectionRow key={q.key} q={q} value={answers[q.key]} allAnswers={answers} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {s.questions.map((q) => (
                      <div key={q.key}>
                        <p className="text-sm text-[#F5F2EE] mb-3">{q.title}</p>
                        {q.subtitle && <p className="text-xs text-[#A29F9B] mb-3">{q.subtitle}</p>}
                        <QuestionEditor q={q}
                          value={q.type === "range_pair" ? null : draft[q.key]}
                          allDraft={draft}
                          onChange={updateField} />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AnswersReview;
